import React, { useState, useEffect, useRef, useCallback } from 'react';
import { styleDefinitions as styles, noteThemes } from '../../theme/styles';
import { FileText, Search, ChevronLeft, X, Palette } from 'lucide-react';

interface NotesViewProps {
  onBack: () => void;
}

export const NotesView = React.memo(({ onBack }: NotesViewProps) => {
  const [content, setContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [highlightedContent, setHighlightedContent] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const themeDropdownRef = useRef<HTMLDivElement>(null);

  // Load notes content and theme from localStorage
  useEffect(() => {
    // Load the saved theme
    const savedTheme = localStorage.getItem('focusHubNoteTheme');
    if (savedTheme && noteThemes[savedTheme]) {
      setSelectedTheme(savedTheme);
    }

    // Use a new key for single note to avoid conflict with old multi-note data
    const savedContent = localStorage.getItem('focusHubSingleNote');

    if (savedContent) {
      setContent(savedContent);
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = savedContent;
      }
    } else {
      // Try to migrate from old notes if they exist
      const oldNotes = localStorage.getItem('focusHubNotes');
      if (oldNotes) {
        try {
          const parsed = JSON.parse(oldNotes);
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Get the content from the most recent note
            const mostRecent = parsed.reduce((latest: any, note: any) => {
              const noteDate = new Date(note.updatedAt || note.createdAt);
              const latestDate = new Date(latest.updatedAt || latest.createdAt);
              return noteDate > latestDate ? note : latest;
            });

            if (mostRecent && mostRecent.content) {
              setContent(mostRecent.content);
              if (contentEditableRef.current) {
                contentEditableRef.current.innerHTML = mostRecent.content;
              }
              // Save to new key
              localStorage.setItem('focusHubSingleNote', mostRecent.content);
            }
          }
        } catch (e) {
          // If it's not JSON, it might be plain text from a previous migration
          // Clear it to start fresh
          console.log('Starting with fresh notes');
        }
        // Remove old notes key to prevent confusion
        localStorage.removeItem('focusHubNotes');
      }
    }

    // Focus the editor on mount
    setTimeout(() => {
      if (contentEditableRef.current) {
        contentEditableRef.current.focus();
      }
    }, 100);
  }, []);

  // Auto-save function
  const autoSave = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsSaving(true);
      localStorage.setItem('focusHubSingleNote', content);
      setHasUnsavedChanges(false);
      // Show saving indicator briefly
      setTimeout(() => {
        setIsSaving(false);
      }, 500);
    }
  }, [hasUnsavedChanges, content]);

  // Debounced auto-save on content change
  useEffect(() => {
    if (hasUnsavedChanges) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 3000); // Auto-save after 3 seconds of inactivity
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [content, hasUnsavedChanges, autoSave]);

  // Save when leaving component
  useEffect(() => {
    return () => {
      if (hasUnsavedChanges) {
        localStorage.setItem('focusHubSingleNote', content);
      }
    };
  }, [hasUnsavedChanges, content]);

  // Handle search - highlight paragraphs containing the search term
  useEffect(() => {
    if (!searchQuery || !contentEditableRef.current) {
      // Clear highlights only if there was previously highlighted content
      if (highlightedContent && contentEditableRef.current) {
        contentEditableRef.current.innerHTML = content;
      }
      setHighlightedContent('');
      return;
    }

    // Parse content into paragraphs and highlight matching ones
    const tempDiv = document.createElement('div');
    // Use the current content from the editor, not from state
    tempDiv.innerHTML = contentEditableRef.current.innerHTML;

    // Get all text nodes and their parent elements
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Node[] = [];
    let node;
    while ((node = walker.nextNode())) {
      if (node.textContent && node.textContent.trim()) {
        textNodes.push(node);
      }
    }

    // Highlight matching text
    const searchLower = searchQuery.toLowerCase();
    textNodes.forEach(textNode => {
      const text = textNode.textContent || '';
      const textLower = text.toLowerCase();

      if (textLower.includes(searchLower)) {
        // Find parent block element (p, div, etc)
        let parent = textNode.parentElement;
        while (parent && parent !== tempDiv && !['P', 'DIV', 'LI'].includes(parent.tagName)) {
          parent = parent.parentElement;
        }

        if (parent && parent !== tempDiv) {
          // Add highlight style to the parent paragraph
          parent.style.backgroundColor = '#fff3cd';
          parent.style.padding = '8px';
          parent.style.borderRadius = '4px';
          parent.style.transition = 'background-color 0.3s ease';
        }

        // Highlight the specific text within
        const regex = new RegExp(`(${searchQuery})`, 'gi');
        const highlighted = text.replace(regex, '<mark style="background-color: #ffc107; padding: 2px 4px; border-radius: 2px;">$1</mark>');

        if (textNode.parentElement) {
          const span = document.createElement('span');
          span.innerHTML = highlighted;
          textNode.parentElement.replaceChild(span, textNode);
        }
      }
    });

    // Update the editable content with highlights
    contentEditableRef.current.innerHTML = tempDiv.innerHTML;
    setHighlightedContent(tempDiv.innerHTML);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, highlightedContent]); // Run when search query changes or highlights are cleared

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    // Only update innerHTML if there was highlighted content
    if (highlightedContent && contentEditableRef.current) {
      contentEditableRef.current.innerHTML = content;
    }
  };

  // Handle theme change
  const handleThemeChange = (themeKey: string) => {
    setSelectedTheme(themeKey);
    localStorage.setItem('focusHubNoteTheme', themeKey);
    setShowThemeDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (themeDropdownRef.current && !themeDropdownRef.current.contains(event.target as Node)) {
        setShowThemeDropdown(false);
      }
    };

    if (showThemeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showThemeDropdown]);

  const theme = noteThemes[selectedTheme] || noteThemes.default;

  return (
    <div style={{
      ...styles.components.notes.whiteboard.container,
      background: theme.container.background,
      border: theme.container.border || `1px solid ${theme.header.borderColor}`,
      boxShadow: theme.container.boxShadow
    }}>
      <div style={{
      ...styles.components.notes.whiteboard.header,
      background: theme.header.background,
      borderBottomColor: theme.header.borderColor
    }}>
        <div style={styles.components.notes.whiteboard.headerLeft}>
          <button
            onClick={onBack}
            style={styles.button.icon}
            title="Back to Work Items"
          >
            <ChevronLeft size={20} />
          </button>
          <FileText size={24} style={styles.components.notes.whiteboard.icon} />
          <h2 style={styles.components.notes.whiteboard.title}>Notes</h2>
        </div>

        <div style={styles.components.notes.whiteboard.searchContainer}>
          <Search size={18} style={styles.components.notes.whiteboard.searchIcon} />
          <input
            type="text"
            placeholder="Search in notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.components.notes.whiteboard.searchInput}
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              style={styles.components.notes.whiteboard.clearButton}
              title="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div ref={themeDropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowThemeDropdown(!showThemeDropdown)}
            style={{
              ...styles.components.notes.whiteboard.themeSelector.button,
              ...(showThemeDropdown ? { backgroundColor: '#f3f4f6' } : {})
            }}
            title="Change theme"
            aria-label="Theme selector"
            aria-expanded={showThemeDropdown}
            aria-haspopup="listbox"
          >
            <Palette size={18} />
            <span>Theme</span>
          </button>
          {showThemeDropdown && (
            <div
              style={styles.components.notes.whiteboard.themeSelector.dropdown}
              role="listbox"
              aria-label="Select a theme"
            >
              {Object.entries(noteThemes).map(([key, noteTheme]) => (
                <div
                  key={key}
                  onClick={() => handleThemeChange(key)}
                  style={{
                    ...styles.components.notes.whiteboard.themeSelector.dropdownItem,
                    ...(selectedTheme === key ? styles.components.notes.whiteboard.themeSelector.dropdownItemActive : {})
                  }}
                  role="option"
                  aria-selected={selectedTheme === key}
                >
                  <div
                    style={{
                      ...styles.components.notes.whiteboard.themeSelector.colorDot,
                      background: noteTheme.editor.background,
                      borderColor: noteTheme.header.borderColor
                    }}
                  />
                  <span>{noteTheme.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {isSaving && (
          <span style={styles.components.notes.whiteboard.saveIndicator}>
            Saved
          </span>
        )}
      </div>

      <div style={{
        ...styles.components.notes.whiteboard.editorContainer,
        background: theme.editor.background
      }}>
        {content === '' && !searchQuery && (
          <div style={styles.components.notes.whiteboard.placeholder}>
            Start writing your notes...
          </div>
        )}
        <div
          ref={contentEditableRef}
          contentEditable={!searchQuery} // Disable editing during search
          style={{
            ...styles.components.notes.whiteboard.editor,
            background: theme.editor.background,
            color: theme.editor.color
          }}
          onInput={(e) => {
            if (!searchQuery) {
              const target = e.currentTarget;
              const html = target.innerHTML;
              setContent(html);
              setHasUnsavedChanges(true);
            }
          }}
          onPaste={(e) => {
            // Prevent default paste to remove all formatting
            e.preventDefault();

            // Get plain text from clipboard
            const plainText = e.clipboardData?.getData('text/plain') || '';

            if (plainText) {
              // Insert plain text without any formatting
              document.execCommand('insertText', false, plainText);
              setHasUnsavedChanges(true);
            }
          }}
          onKeyDown={(e) => {
            if (searchQuery) return; // Disable editing during search

            // Handle bold shortcut (Ctrl/Cmd + B)
            if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
              e.preventDefault();
              document.execCommand('bold', false);
              setHasUnsavedChanges(true);
            }

            // Handle italic shortcut (Ctrl/Cmd + I)
            if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
              e.preventDefault();
              document.execCommand('italic', false);
              setHasUnsavedChanges(true);
            }

            // Handle underline shortcut (Ctrl/Cmd + U)
            if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
              e.preventDefault();
              document.execCommand('underline', false);
              setHasUnsavedChanges(true);
            }

            // Handle bullet points with "-" + space
            if (e.key === ' ') {
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const container = range.startContainer;
                const text = container.textContent || '';
                const offset = range.startOffset;

                // Check if we just typed a dash and are about to type space
                if (offset > 0 && text[offset - 1] === '-') {
                  // Get the parent element
                  let parentElement = container.parentElement;
                  if (!parentElement) return;

                  // Check if this is at the start of a block element or after a line break
                  const beforeDash = text.substring(0, offset - 1).trim();

                  // Only convert to bullet if dash is at the beginning
                  if (beforeDash === '') {
                    e.preventDefault();

                    // Simply replace the dash with bullet using selection API
                    const sel = window.getSelection();
                    if (sel && sel.rangeCount > 0) {
                      // Move back one character to select the dash
                      const newRange = document.createRange();
                      newRange.setStart(container, offset - 1);
                      newRange.setEnd(container, offset);
                      sel.removeAllRanges();
                      sel.addRange(newRange);

                      // Replace with bullet and space
                      document.execCommand('delete', false);
                      document.execCommand('insertText', false, '• ');

                      setHasUnsavedChanges(true);
                    }
                  }
                }
              }
            }

            // Handle Enter for continuing bullet points
            if (e.key === 'Enter') {
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const container = range.startContainer;
                const offset = range.startOffset;
                const text = container.textContent || '';

                // Check if we're in a bullet point context
                // Look for bullet in the current line
                const beforeCursor = text.substring(0, offset);

                // Find the last line break before cursor or start of text
                const lastLineBreak = beforeCursor.lastIndexOf('\n');
                const currentLineStart = lastLineBreak === -1 ? 0 : lastLineBreak + 1;
                const currentLineBeforeCursor = beforeCursor.substring(currentLineStart);

                // Check if current line starts with bullet
                if (currentLineBeforeCursor.trimStart().startsWith('•')) {
                  e.preventDefault();

                  // Check if the current line only has bullet (empty bullet point)
                  const currentLineContent = currentLineBeforeCursor.trim();
                  if (currentLineContent === '•' || currentLineContent === '• ') {
                    // Remove the bullet point and stay on the same line
                    // Select and delete the bullet on current line
                    const deleteRange = document.createRange();
                    deleteRange.setStart(container, currentLineStart);
                    deleteRange.setEnd(container, offset);
                    selection.removeAllRanges();
                    selection.addRange(deleteRange);
                    document.execCommand('delete', false);

                    // Don't insert a line break - cursor stays where bullet was
                  } else {
                    // Current bullet has content, create new bullet on next line
                    document.execCommand('insertLineBreak');
                    document.execCommand('insertText', false, '• ');
                  }
                  setHasUnsavedChanges(true);
                }
              }
            }
          }}
          suppressContentEditableWarning={true}
        />
        {searchQuery && !highlightedContent && (
          <div style={styles.components.notes.whiteboard.noResults}>
            No results found for "{searchQuery}"
          </div>
        )}
      </div>
    </div>
  );
});